import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const STEP_NAMES: Record<number, string> = {
  0: "Leeftijd",
  1: "Huidprobleem",
  2: "Producten",
  3: "Insight",
  4: "Tevredenheid",
  5: "Kliniek",
  6: "Product Falen",
  7: "Huidwetenschap",
  8: "Droomresultaat",
  9: "Collageen",
  10: "Persoonlijk Profiel",
  11: "Ingrediënten",
  12: "Product Match",
  13: "Investering",
};

function generateSessionId(): string {
  return Math.random().toString(36).substring(2, 7);
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "desktop";
}

function getUTMParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get("utm_source") || undefined,
    utm_medium: params.get("utm_medium") || undefined,
    utm_campaign: params.get("utm_campaign") || undefined,
    utm_content: params.get("utm_content") || undefined,
    utm_term: params.get("utm_term") || undefined,
  };
}

export function useQuizTracking(step: number, totalSteps: number) {
  const sessionIdRef = useRef<string | null>(null);
  const abVariantRef = useRef<string>("none");
  const isInitializedRef = useRef(false);
  const backClicksRef = useRef(0);
  const prevStepRef = useRef(step);
  const [abVariant, setAbVariant] = useState<string>("none");

  // Initialize session + record pageview
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const init = async () => {
      const sessionId = generateSessionId();
      sessionIdRef.current = sessionId;
      const device = getDeviceType();
      const utm = getUTMParams();

      // Balance A/B variants based on pageview counts since current test start
      const AB_TEST_START = '2026-02-27T10:19:00Z';
      let variant = "A";
      try {
        const [countA, countB] = await Promise.all([
          supabase.from("quiz_pageviews").select("id", { count: "exact", head: true }).eq("ab_variant", "A").gte("viewed_at", AB_TEST_START),
          supabase.from("quiz_pageviews").select("id", { count: "exact", head: true }).eq("ab_variant", "B").gte("viewed_at", AB_TEST_START),
        ]);
        const nA = countA.count ?? 0;
        const nB = countB.count ?? 0;
        variant = nA <= nB ? "A" : "B";
      } catch {
        variant = Math.random() < 0.5 ? "A" : "B";
      }
      abVariantRef.current = variant;
      setAbVariant(variant);
      sessionStorage.setItem("ab_variant", variant);

      // Record pageview (fire and forget)
      supabase.from("quiz_pageviews").insert({
        session_id: sessionId,
        referrer: document.referrer || null,
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        device_type: device,
        user_agent: navigator.userAgent,
        ab_variant: variant,
      }).then(() => {});

      // Create session FIRST, then record step event
      supabase.from("quiz_sessions").insert({
        id: sessionId,
        device_type: device,
        user_agent: navigator.userAgent,
        utm_source: utm.utm_source || null,
        utm_medium: utm.utm_medium || null,
        utm_campaign: utm.utm_campaign || null,
        utm_content: utm.utm_content || null,
        utm_term: utm.utm_term || null,
        referrer: document.referrer || null,
        current_step: 0,
        ab_variant: variant,
      }).then(({ error }) => {
        if (error) return;
        // Only insert step event after session exists
        supabase.from("quiz_step_events").insert({
          session_id: sessionId,
          step: 0,
          step_name: STEP_NAMES[0],
        }).then(() => {});
      });
    };

    init();
  }, []);

  // Track step changes
  useEffect(() => {
    if (!sessionIdRef.current) return;
    if (prevStepRef.current === step) return;

    const prevStep = prevStepRef.current;
    prevStepRef.current = step;

    // Track back clicks
    if (step < prevStep) {
      backClicksRef.current += 1;
    }

    // Close previous step event by session_id + step
    supabase.from("quiz_step_events")
      .update({ left_at: new Date().toISOString() })
      .eq("session_id", sessionIdRef.current)
      .eq("step", prevStep)
      .is("left_at", null)
      .then(() => {});

    const isCompleted = step === totalSteps - 1;

    // Update session
    supabase.from("quiz_sessions")
      .update({
        current_step: step,
        last_seen_at: new Date().toISOString(),
        max_step_reached: step,
        back_clicks: backClicksRef.current,
        ...(isCompleted ? { is_completed: true, completed_at: new Date().toISOString() } : {}),
      })
      .eq("id", sessionIdRef.current)
      .then(() => {});

    // Create new step event
    supabase.from("quiz_step_events").insert({
      session_id: sessionIdRef.current,
      step,
      step_name: STEP_NAMES[step] || `Scherm ${step}`,
    }).then(() => {});
  }, [step, totalSteps]);

  // Update answers
  const trackAnswers = useCallback((answers: Record<string, unknown>) => {
    if (!sessionIdRef.current) return;
    supabase.from("quiz_sessions")
      .update({ answers: answers as any })
      .eq("id", sessionIdRef.current)
      .then(() => {});
  }, []);

  // Heartbeat to track active sessions
  useEffect(() => {
    const interval = setInterval(() => {
      if (!sessionIdRef.current) return;
      supabase.from("quiz_sessions")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("id", sessionIdRef.current)
        .then(() => {});
    }, 30000); // every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return { trackAnswers, abVariant };
}
